import { SafeResourceUrl } from '@angular/platform-browser';
import { PhoneCallTypes, SmsCallDetails } from 'voxloud-types/dist/models';

export interface Filter {
  name: string;
  locked: boolean;
}

export enum CallType {
  phone_call = 'PHONE_CALL',
  click_to_call = 'CLICK_TO_CALL',
  fax_call = 'FAX_CALL',
  whatsapp = 'WHATSAPP',
}

export enum CallDirection {
  inbound = 'INBOUND',
  outbound = 'OUTBOUND',
  internal = 'INTERNAL',
}

export enum CallStatus {
  answered = 'ANSWERED',
  unanswered = 'UNANSWERED',
}

export interface CallLogDidFilter {
  id: number;
  label: string;
  number: string;
}

export interface CallLogUserFilter {
  id: number;
  first_name: string;
  last_name: string;
  aggregate_id: string;
}

export interface CallLogRange {
  from?: Date | string;
  to?: Date | string;
}

export interface CallLogDateTimeRange extends CallLogRange {
  time: CallLogRange;
  from_time?: string;
  to_time?: string;
}

export interface CallLogDateTimeRangeDTO {
  from: string | Date;
  to: string | Date;
  from_time?: string;
  to_time?: string;
}

export interface CallLogFilters {
  date_time?: CallLogRange;
  date_time_ranges?: CallLogDateTimeRange[];
  timezone?: string;
  did_number?: CallLogDidFilter;
  direction?: CallDirection;
  destination_name?: string;
  destination_number?: string;
  originator_name?: string;
  originator_number?: string;
  answerer_name?: string;
  answerer_number?: string;
  duration?: {
    from?: string;
    to?: string;
  };
  ring_time?: {
    from?: string;
    to?: string;
  };
  status?: CallStatus;
  event_types?: CallType;
  sentiment_score?: {
    from?: string;
    to?: string;
  };
  voicemail?: boolean;
  recorded?: boolean;
  with_sms?: boolean;
  is_ai_agent?: boolean;
  user?: CallLogUserFilter;
}

export interface CallLogFiltersDTO {
  account_id: string;
  date_time?: {
    from: string;
    to: string;
  };
  date_time_ranges?: CallLogDateTimeRangeDTO[];
  event_types?: CallType[];
  destination_name?: string;
  destination_number?: string;
  originator_name?: string;
  originator_number?: string;
  answerer_name?: string;
  answerer_number?: string;
  direction?: CallDirection;
  duration?: {
    from: number;
    to: number;
  };
  ring_time?: {
    from: number;
    to: number;
  };
  is_answered?: boolean;
  did_number?: string;
  sentiment_score?: {
    from?: string;
    to?: string;
  };
  voicemail?: boolean;
  recorded?: boolean;
  with_sms?: boolean;
  is_ai_agent?: boolean;
  user_id?: string;
  is_whatsapp?: boolean;
}

export const CALL_STATUSES = [CallStatus.answered, CallStatus.unanswered];
export const CALL_DIRECTIONS = [CallDirection.internal, CallDirection.inbound, CallDirection.outbound];
export const CALL_TYPES = [CallType.phone_call, CallType.fax_call, CallType.click_to_call];
export const AI_PROCESSING_FAILED = 'failed';
export const AI_PROCESSING = 'processing';
export const YES_NO_VALUES = ['YES', 'NO'];

export enum CALL_LOG_EVENT_DIRECTION {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
  INTERNAL = 'INTERNAL',
}

export enum CALL_LOG_EVENT_TYPE {
  CALL_REQUEST = 'CALL_REQUEST',
  CALL_STARTED = 'CALL_STARTED',
  CALL_CANCELED_BY_ORIGINATOR = 'CALL_CANCELED_BY_ORIGINATOR',
  CALL_ANSWERED = 'CALL_ANSWERED',
  CALL_TERMINATED = 'CALL_TERMINATED',
  CALL_REJECTED = 'CALL_REJECTED',
  CALL_LOST_LOSE_RACE = 'CALL_LOST_LOSE_RACE',
  CALL_LOST_NO_ANSWER = 'CALL_LOST_NO_ANSWER',
  CALL_TRANSFERRED = 'CALL_TRANSFERRED',
  CALL_LOST_USER_BUSY = 'CALL_LOST_USER_BUSY',
  CALL_LOST_BUSY_LINES = 'CALL_LOST_BUSY_LINES',
}

export const CALL_ENDING_TYPE = {
  OUTBOUND: {
    ANSWERED: 'outbound_answered',
    UNANSWERED: 'outbound_unanswered',
  },
  INTERNAL: {
    ANSWERED: 'internal_answered',
    UNANSWERED: 'internal_unanswered',
  },
  INBOUND: {
    ANSWERED: 'inbound_answered',
    UNANSWERED: 'inbound_unanswered',
  },
};

interface LogItem {
  destinationName: string;
  destinationNumber: string;
  event: CALL_LOG_EVENT_TYPE;
  id: number;
  originatorName: string;
  originatorNumber: string;
  timestamp: Date;
}

interface Pageable {
  offset: number;
  pageNumber: number;
  pageSize: number;
  paged: boolean;
  sort: Sort;
  unpaged: boolean;
}
interface Sort {
  empty: boolean;
  sorted: boolean;
  unsorted: boolean;
}
export interface CallLogResponse {
  content: CallLogEvent[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  pageable: Pageable;
  size: number;
  sort: Sort;
  totalElements: number;
  totalPages: number;
  synchronous_export: boolean;
}

export interface CallLogEvent {
  accountId: string;
  archived: boolean;
  callId: string;
  crmLogo: string;
  contactCrm?: string;
  contactCrmUrl?: string;
  answererName?: string;
  answererNumber?: string;
  destinationName: string;
  destinationNumber: string;
  direction: CALL_LOG_EVENT_DIRECTION;
  duration: number;
  id: number;
  lastEvent: CALL_LOG_EVENT_TYPE;
  logItems: LogItem[];
  originatorName: string;
  originatorNumber: string;
  recorded: boolean;
  timestamp: Date;
  labels: string[];
  ruleNames: string[];
  type: PhoneCallTypes;
  recordingUrl?: string;
  ringTime: number;
  sentiment_score: number;
  transcription: string;
  summary: string;
  maskCallerNumber: boolean;
  webhooks?: WebhookLog[];
  voicemail: boolean;
  voicemailUrl: string;
  sms?: SmsCallDetails[];
}

export interface ExportData {
  url: string;
  message: string;
  isXLSXFormat: boolean;
}

export interface UICallLogEvent {
  id: number;
  type: PhoneCallTypes;
  number: string;
  from: string;
  to: string;
  fromFull: string;
  toFull: string;
  answererFull: string;
  contactCrm: string;
  contactCrmUrl: string;
  contactCrmIconLink: string;
  answerer: string;
  duration: string | number;
  direction: CALL_LOG_EVENT_DIRECTION;
  timestamp: Date | string;
  labels: string[];
  ruleNames: string[];
  recorded: boolean;
  recordingUrl: string;
  answered: boolean;
  accountId: string;
  ringTime: string | number;
  maskCallerNumber: boolean;
  callLinks: {
    from: SafeResourceUrl;
    to: SafeResourceUrl;
    answerer: SafeResourceUrl;
  };
  ai: AIItem;
  webhooks: WebhookLog[];
  voicemail: boolean;
  voicemailUrl: string;
  sms: SmsCallDetails[];
}

export interface AIItem {
  sentimentScore: number;
  transcription: string;
  summary: string;
  isProcessing: boolean;
  processingFailed: boolean;
  hasData: boolean;
}

export interface EventObject {
  event: string;
  value: {
    limit: number;
    page: number;
    key: string;
    order: string;
  };
}

export interface WebhookLog {
  id: string;
  call_id: string;
  url: string;
  rule_name: string;
  attempt: number;
  status: WEBHOOK_STATUS;
  error_message: string;
  response_status: number;
  executed_at: Date;
}

export enum WEBHOOK_STATUS {
  SENT = 'SENT',
  FAILED = 'FAILED',
}

export const DEFAULT_CALL_HISTORY_PAGE_SIZE = 20;
